import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router'
import Papa from 'papaparse'
import { FaTrash, FaFilePdf, FaFileWord, FaFileExport, FaArrowUp, FaArrowDown, FaCopy, FaChevronDown, FaFileCsv, FaEllipsisV, FaBook, FaLightbulb } from 'react-icons/fa'
import { LuSparkles } from 'react-icons/lu'
import { Badge, Heading, Text, Box, Stack, Button, Icon, EmptyBox, ConfirmButton, SimpleModal, SimpleTable, Input, Alert, List, Menu, MenuItem, Checkbox, Textarea, FormControl, FileChooser, EmptyCard } from '@/components/lib'

import api from '@/api'
import utils from '@/utils/utils'
import useStore from '@/store'
import useTitle from '@/hooks/useTitle'
import useJob from '@/hooks/useJob'

// Starting points for the generate-stages box, for an admin who can see what
// the feature does but not what a description that works actually looks like.
// Each one is a whole stage rather than a fragment, and each asks for at least
// one branching question, since that is the part least likely to be guessed.
const EXAMPLE_PROMPTS = [
  'A screening form with Age (integer) and Sex (choice: Male, Female, Other). Ask whether the participant smokes, and only if they do, how many a day. A baseline form with weight (kg), height (cm), and blood pressure.',
  'An eligibility screening questionnaire: date of informed consent, age in years, and a yes/no for each inclusion criterion (confirmed diagnosis, able to attend all visits, willing to use the app). Ask whether they are taking any medication, and only if they are, the name, the dose and how long they have been on it. Finish with a free-text box for the screening nurse.',
  'A baseline visit form. Demographics first: date of birth, sex, ethnicity. Then height and weight, smoking status (Never, Former, Current) and alcohol units per week — for former and current smokers, also ask the age they started. Finish with a vital signs section: blood pressure, heart rate and temperature.',
  'A weekly symptom diary the participant fills in themselves. Rate fatigue, pain and sleep quality on a 0-10 slider each, ask how many days this week symptoms stopped them doing normal activities, and whether anything changed in their medication. If something did change, ask what changed.',
  'An adverse event report: date of onset, a description of the event, severity (Mild, Moderate, Severe) and whether it is serious. If it is serious, ask which seriousness criteria apply and the date the site was notified. Then ask whether it is related to the study treatment, what action was taken, and the current outcome.'
]

export default function Stages () {
  const { user, team, trial, updateTrial, stages, addStage, deleteStage, addDataType } = useStore()
  const navigate = useNavigate()
  const [importingRedcap, setImportingRedcap] = useState(false)
  const [importReport, setImportReport] = useState(null)
  const [selectedStages, setSelectedStages] = useState([])
  const [exportingStages, setExportingStages] = useState(new Set())
  const [trialPickerStage, setTrialPickerStage] = useState(null)
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [generatePrompt, setGeneratePrompt] = useState('')
  const [generatingStages, setGeneratingStages] = useState(false)
  const [lastExampleIndex, setLastExampleIndex] = useState(-1)
  const [nameFilter, setNameFilter] = useState('')
  const [showLibraryModal, setShowLibraryModal] = useState(false)
  const [importingFromLibrary, setImportingFromLibrary] = useState(false)
  const isImportOrGenerateLoading = importingRedcap || generatingStages || importingFromLibrary
  const { startPolling } = useJob()
  useTitle('Stages', trial)

  function onDelete (id) {
    api.stages.delete(id, () => {
      utils.toast('success', 'Stage deleted')
      deleteStage(id)
      updateTrial(trial._id, { stageCount: (trial.stageCount ?? 1) - 1 })
    }, err => utils.toast('error', 'Unable to delete stage', err.message))
  }

  function bulkDelete () {
    api.stages.deleteBulk(selectedStages, () => {
      selectedStages.forEach(id => deleteStage(id))
      setSelectedStages([])
      updateTrial(trial._id, { stageCount: Math.max(0, (trial.stageCount ?? 0) - selectedStages.length) })
      utils.toast('success', 'Selected stages deleted')
    }, err => utils.toast('error', 'Unable to delete stages', err.message))
  }

  function clone (trialId, s) {
    api.trials.createStage(trialId, { copyFrom: s._id }, stage => {
      utils.toast('success', 'Stage created')
      if (trialId === trial._id) {
        addStage(stage)
        updateTrial(trialId, { stageCount: (trial.stageCount ?? 0) + 1 })
      }
    }, err => utils.toast('error', 'Unable to clone stage', err.message))
  }

  function move (s, direction) {
    const newIndex = (s.index || 0) + direction
    if (newIndex < 0 || newIndex >= stages.length) return
    api.stages.update(s._id, { index: newIndex }, () => {
      api.trials.getStages(trial._id, d => d.stages.forEach(addStage))
    })
  }

  function exportStage (stageId, stageName, format) {
    setExportingStages(prev => new Set([...prev, stageId]))
    api.stages.getWithFormat(stageId, format, pdf => {
      const link = document.createElement('a')
      link.href = pdf?.url
      link.setAttribute('download', `${stageName}.pdf`)
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      setExportingStages(prev => { const next = new Set(prev); next.delete(stageId); return next })
    }, err => {
      utils.toast('error', 'Unable to export stage', err.message)
      setExportingStages(prev => { const next = new Set(prev); next.delete(stageId); return next })
    })
  }

  function importFromREDCap (files) {
    if (!files?.length) return
    setImportingRedcap(true)
    setImportReport(null)
    const reader = new window.FileReader()
    reader.onload = (e) => {
      const text = e.target.result
      const data = Papa.parse(text, { header: true }).data
      api.trials.importStages(trial._id, 'importFromREDCap', { importData: { fields: data } }, (res) => {
        const { stages: newStages, dataTypes: newDataTypes, createdStages, createdDataTypes, warnings = [] } = res
        newStages.forEach(addStage)
        newDataTypes.forEach(addDataType)
        setImportReport({
          stagesCount: createdStages?.length ?? newStages?.length ?? 0,
          dataTypesCount: createdDataTypes?.length ?? newDataTypes?.length ?? 0,
          warnings
        })
        utils.toast('success', 'Import complete')
        setImportingRedcap(false)
      }, err => {
        utils.toast('error', 'Unable to import stages', err.message)
        setImportingRedcap(false)
      })
    }
    reader.readAsText(files[0])
  }

  // Called with a handler when it acts on click, and without one when it is a
  // ConfirmButton's trigger — the popover owns the click in that case.
  function exampleButton (onClick) {
    return (
      <Button size='sm' variant='outline' disabled={generatingStages} onClick={onClick}>
        <Icon as={FaLightbulb} /> Give me an example
      </Button>
    )
  }

  function fillExamplePrompt () {
    // Never the same one twice running: with five to choose from a repeat is
    // likely enough, and it reads as the button having done nothing.
    let index = Math.floor(Math.random() * EXAMPLE_PROMPTS.length)
    if (index === lastExampleIndex) index = (index + 1) % EXAMPLE_PROMPTS.length
    setLastExampleIndex(index)
    setGeneratePrompt(EXAMPLE_PROMPTS[index])
  }

  function generateStages () {
    const prompt = (generatePrompt || '').trim()
    if (!prompt) {
      utils.toast('error', 'Please enter a description of the forms and fields you want to create')
      return
    }
    setGeneratingStages(true)
    setImportReport(null)
    api.trials.generateStages(trial._id, prompt, ({ jobId }) => {
      startPolling(jobId, {
        onDone: result => {
          const { stages: newStages = [], dataTypes: newDataTypes = [], createdStages, createdDataTypes, warnings = [] } = result
          newStages.forEach(addStage)
          newDataTypes.forEach(addDataType)
          setImportReport({
            stagesCount: createdStages?.length ?? newStages?.length ?? 0,
            dataTypesCount: createdDataTypes?.length ?? newDataTypes?.length ?? 0,
            warnings
          })
          utils.toast('success', 'Generate complete')
          setGeneratingStages(false)
          setShowGenerateModal(false)
          setGeneratePrompt('')
        },
        onError: message => {
          utils.toast('error', 'Unable to generate stages', message)
          setGeneratingStages(false)
        }
      })
    }, err => {
      utils.toast('error', 'Unable to generate stages', err.message)
      setGeneratingStages(false)
    })
  }

  function importFromLibrary (questionnaireId) {
    setImportingFromLibrary(true)
    setImportReport(null)
    api.trials.importFromLibrary(trial._id, questionnaireId, (res) => {
      const { stages: newStages, dataTypes: newDataTypes, createdStages, createdDataTypes, warnings = [] } = res
      newStages.forEach(addStage)
      newDataTypes.forEach(addDataType)
      setImportReport({
        stagesCount: createdStages?.length ?? newStages?.length ?? 0,
        dataTypesCount: createdDataTypes?.length ?? newDataTypes?.length ?? 0,
        warnings
      })
      utils.toast('success', 'Questionnaire added')
      setImportingFromLibrary(false)
      setShowLibraryModal(false)
    }, err => {
      utils.toast('error', 'Unable to add questionnaire', err.message)
      setImportingFromLibrary(false)
    })
  }

  // Stage CRUD is gated on trial.write on the API (can_write_trial).
  const canEdit = utils.canWriteTrial(user, trial)

  // Name filter, applied here rather than by refetching: the whole stage list
  // is already in the store, and a trial has tens of stages, not thousands.
  const filterTerm = nameFilter.trim().toLowerCase()
  const isFiltering = filterTerm.length > 0
  const visibleStages = isFiltering
    ? stages.filter(s => (s.name ?? '').toLowerCase().includes(filterTerm))
    : stages

  return (
    <>
      <Stack gap={5}>
        <Stack direction='row' justifyContent='space-between'>
          <Box>
            <Heading as='h2' size='lg'>Trial stages</Heading>
            <Text>The forms that allow for trial data collection.</Text>
          </Box>
          <Stack direction='row' gap={2}>
            {canEdit && (utils.hasFeature(team, 'generateStagesAI') || utils.hasFeature(team, 'redcapImport') || utils.hasFeature(team, 'questionnaireLibrary')) &&
              <Menu
                closeOnSelect={false}
                trigger={
                  <Button
                    colorPalette='primary'
                    variant='outline'
                    loading={isImportOrGenerateLoading}
                    rightIcon={<Icon as={FaChevronDown} />}
                  >
                    Import or generate
                  </Button>
                }
              >
                {utils.hasFeature(team, 'generateStagesAI') &&
                  <MenuItem value='generate' onClick={() => setShowGenerateModal(true)}>
                    <Icon as={LuSparkles} /> Generate stages
                  </MenuItem>}
                {utils.hasFeature(team, 'questionnaireLibrary') &&
                  <MenuItem value='library' onClick={() => setShowLibraryModal(true)}>
                    <Icon as={FaBook} /> Browse the questionnaire library
                  </MenuItem>}
                {utils.hasFeature(team, 'redcapImport') &&
                  <FileChooser
                    accept='.csv' asFileInput
                    onComplete={files => importFromREDCap(files)}
                    trigger={
                      <MenuItem value='redcapImport'>
                        <Icon as={FaFileCsv} />
                        Import from REDCap CSV file
                      </MenuItem>
                    }
                  />}
              </Menu>}
            {canEdit &&
              <Button as={Link} to={`/trials/${trial._id}/stages/new`} colorPalette='primary'>Create a new stage</Button>}
          </Stack>
        </Stack>

        {stages?.length > 0
          ? (
            <Stack gap={3}>
              <Stack direction='row' alignItems='center' gap={3} flexWrap='wrap'>
                <Input
                  value={nameFilter}
                  onChange={e => setNameFilter(e.target.value)}
                  placeholder='Filter stages by name...'
                  maxW='420px'
                  size='sm'
                />
                {isFiltering &&
                  <Text fontSize='sm' color='fg.muted'>
                    Showing {visibleStages.length} of {stages.length}
                    {canEdit && '. Clear the filter to reorder stages.'}
                  </Text>}
              </Stack>
              {visibleStages.length === 0
                ? <EmptyBox icon='box' title='No matching stages' description={`Nothing here is named like "${nameFilter.trim()}".`} />
                : (
                  <SimpleTable
                    size='sm'
                    asCard
                    onRowClick={(row, index) => navigate(`${visibleStages[index]._id}`)}
                    headers={[
                      ...(canEdit
                        ? [
                          <Menu
                            key='bulk-select'
                            closeOnSelect={false}
                            trigger={
                              <Button size='xs' variant='ghost'>
                                {selectedStages?.length || 0} <Icon><FaChevronDown /></Icon>
                              </Button>
                          }
                          >
                            <MenuItem value='none' onClick={() => setSelectedStages([])}>De-select all</MenuItem>
                            <MenuItem value='all' onClick={() => setSelectedStages(visibleStages.map(s => s._id))}>{isFiltering ? 'Select all visible stages' : 'Select all stages'}</MenuItem>
                            {selectedStages?.length > 0 &&
                              <>
                                <Box borderBottomWidth='1px' borderColor='border.muted' />
                                <ConfirmButton
                                  trigger={<MenuItem value='delete'>Delete selected stages</MenuItem>}
                                  header='Really delete the selected stages?'
                                  footer='Any data collected by participants for these stages will be lost.'
                                  onConfirm={() => bulkDelete()}
                                  actionColor='red'
                                />
                              </>}
                          </Menu>]
                        : []),
                      'Name', 'Status', ...(canEdit ? [''] : [])]}
                    rows={visibleStages.map(s => {
                      const isChecked = selectedStages?.includes(s._id)
                      const isExporting = exportingStages.has(s._id)
                      return [
                      // Checkbox (only rendered when canEdit)
                        ...(canEdit
                          ? [
                            <Checkbox
                              key='check'
                              isChecked={isChecked}
                              onChange={() => {
                                if (isChecked) setSelectedStages(prev => prev.filter(id => id !== s._id))
                                else setSelectedStages(prev => [...prev, s._id])
                              }}
                            />]
                          : []),

                        <Heading key='name' as='h4' size='sm'>
                          <Link to={`${s._id}`}>{s.name}</Link>
                        </Heading>,

                        <Stack key='badges' direction='row' flexWrap='wrap' gap={1}>
                          <Badge colorPalette='blue'>
                            {s.dataTypeCount} {s.dataTypeCount === 1 ? 'question' : 'questions'}
                          </Badge>
                          {s.isRecurring && s.recurringFrequency > 0 &&
                            <Badge colorPalette='primary'>
                              Every {s.recurringFrequency} {s.recurringFrequency === 1 ? 'day' : 'days'} × {s.recurringDuration || 0}
                            </Badge>}
                          {s.adHoc && <Badge colorPalette='primary'>Ad-hoc</Badge>}
                          {s.reminderCount > 0 && <Badge colorPalette='blue'>{s.reminderCount} {s.reminderCount === 1 ? 'reminder' : 'reminders'}</Badge>}
                          {s.isPoll && <Badge colorPalette='yellow'>Poll</Badge>}
                        </Stack>,

                        // Actions (only rendered when canEdit)
                        ...(canEdit
                          ? [
                            <Stack key='actions' direction='row' alignItems='center' justifyContent='end' gap={1} flexWrap='nowrap'>
                              <Stack direction='row' gap={1}>
                                <Button size='xs' isDisabled={isFiltering || s.index <= 0} onClick={() => move(s, -1)}><Icon as={FaArrowUp} /></Button>
                                <Button size='xs' isDisabled={isFiltering || s.index >= stages.length - 1} onClick={() => move(s, 1)}><Icon as={FaArrowDown} /></Button>
                              </Stack>
                              <Menu
                                trigger={
                                  <Button size='sm' variant='ghost' loading={isExporting}>
                                    <Icon as={FaEllipsisV} />
                                  </Button>
                                }
                              >
                                <MenuItem value='edit' onClick={() => navigate(`${s._id}`)}>Edit</MenuItem>
                                <MenuItem value='copy' onClick={() => clone(trial?._id, s)}>
                                  <Icon as={FaCopy} /> Make a copy
                                </MenuItem>
                                <MenuItem value='copyToTrial' onClick={() => setTrialPickerStage(s)}>
                                  <Icon as={FaFileExport} /> Copy to another trial
                                </MenuItem>
                                <Box borderBottomWidth='1px' borderColor='border.muted' my={1} />
                                <MenuItem value='pdf' onClick={() => exportStage(s._id, s.name, 'pdf')}>
                                  <Icon as={FaFilePdf} /> Download as PDF
                                </MenuItem>
                                <MenuItem value='docx' onClick={() => exportStage(s._id, s.name, 'docx')}>
                                  <Icon as={FaFileWord} /> Download as Word
                                </MenuItem>
                                <Box borderBottomWidth='1px' borderColor='border.muted' my={1} />
                                <ConfirmButton
                                  asSubMenu
                                  trigger={<MenuItem value='delete' color='red.500'><Icon as={FaTrash} /> Delete</MenuItem>}
                                  header='Really delete this stage?'
                                  body='Any data collected by participants for this stage will be lost.'
                                  onConfirm={() => onDelete(s._id)}
                                  actionColor='red'
                                />
                              </Menu>
                            </Stack>
                            ]
                          : [])
                      ]
                    })}
                  />)}
            </Stack>)
          : (
            <Stack>
              <Stack direction='row' justifyContent='center'>
                <EmptyCard icon='form' title='Stages' description='Stages represent the forms and questionnaires that will be used to collect your data. They can be made available directly to participants (ePRO) or to staff (eCRF).' />
              </Stack>
              <EmptyBox title='Get started' description={canEdit ? 'Create your first stage using the button below.' : 'No stages have been created yet.'}>
                {canEdit &&
                  <Button colorPalette='primary' as={Link} to={`/trials/${trial._id}/stages/new`}>Create your first stage</Button>}
              </EmptyBox>
            </Stack>)}
      </Stack>

      {showLibraryModal && (
        <QuestionnaireLibraryModal
          isOpen={showLibraryModal}
          onClose={() => !importingFromLibrary && setShowLibraryModal(false)}
          onImport={importFromLibrary}
          importing={importingFromLibrary}
          trialId={trial._id}
        />
      )}

      {trialPickerStage && (
        <TrialPicker
          isOpen={!!trialPickerStage}
          onClose={() => setTrialPickerStage(null)}
          onSelected={trialId => { clone(trialId, trialPickerStage); setTrialPickerStage(null) }}
        />
      )}

      {showGenerateModal && (
        <SimpleModal
          isOpen={showGenerateModal}
          onClose={() => !generatingStages && setShowGenerateModal(false)}
          header='Generate stages'
          modalProps={{ size: 'lg' }}
          body={
            <Stack gap={4}>
              <Text>
                Describe the forms (stages) and fields you want in plain English, or paste content from a document (e.g. Word, CSV, Excel). The system will create the corresponding stages and data types.
              </Text>
              <FormControl label='Description or prompt'>
                <Textarea
                  value={generatePrompt}
                  onChange={e => setGeneratePrompt(e.target.value)}
                  placeholder='e.g. A screening form with Age (integer) and Sex (choice: Male, Female, Other). Ask whether the participant smokes, and only if they do, how many a day. A baseline form with weight (kg), height (cm), and blood pressure.'
                  rows={8}
                  resize='vertical'
                />
              </FormControl>
              <Stack direction='row'>
                {generatePrompt.trim()
                  ? (
                    <ConfirmButton
                      trigger={exampleButton()}
                      header='Replace what you have written?'
                      body='The example will be put in the box instead of your current description.'
                      onConfirm={fillExamplePrompt}
                      actionColor='primary'
                    />)
                  : exampleButton(fillExamplePrompt)}
              </Stack>
            </Stack>
          }
          footer={
            <Stack direction='row' gap={2}>
              <Button variant='ghost' onClick={() => setShowGenerateModal(false)} isDisabled={generatingStages}>Cancel</Button>
              <Button colorPalette='primary' loading={generatingStages} onClick={generateStages}>Generate</Button>
            </Stack>
          }
        />
      )}

      {importReport && (
        <SimpleModal
          isOpen={!!importReport}
          onClose={() => setImportReport(null)}
          header='Import report'
          modalProps={{ size: 'md' }}
          body={
            <Stack gap={4}>
              <Text>
                Successfully imported <strong>{importReport.stagesCount}</strong> {importReport.stagesCount === 1 ? 'stage' : 'stages'} and <strong>{importReport.dataTypesCount}</strong> data {importReport.dataTypesCount === 1 ? 'type' : 'types'}.
              </Text>
              {importReport.warnings?.length > 0 && (
                <Alert status='warning' flexDirection='column' alignItems='stretch'>
                  <Heading size='sm' mb={2}>Warnings ({importReport.warnings.length})</Heading>
                  <List
                    items={importReport.warnings.map((w, i) => ({
                      key: i,
                      content: typeof w === 'object' && w.message ? w.message : String(w)
                    }))}
                    spacing={1}
                  />
                </Alert>
              )}
            </Stack>
          }
          footer={<Button onClick={() => setImportReport(null)}>Close</Button>}
        />
      )}
    </>
  )
}

function TrialPicker ({ isOpen, onClose, onSelected }) {
  const [trials, setTrials] = useState([])
  const [rootTrialId, setRootTrialId] = useState('')
  const [loading, setLoading] = useState(true)
  const { user } = useStore()

  useEffect(() => {
    if (!user?.team || !isOpen) return
    setLoading(true)
    api.teams.getTrials(user?.team, { includeArchived: false }, ({ trials }) => {
      setTrials(trials)
      setLoading(false)
    })
  }, [user, isOpen])

  return (
    <SimpleModal
      isOpen={isOpen} onClose={onClose}
      header='Select a trial to copy the selected stage to'
      body={
        <Stack spacing={3}>
          <Text>Any data types used in the selected stage will be re-created in the selected trial.</Text>
          <Heading size='sm'>Select a trial to copy this stage to</Heading>

          {utils.hasPermission(user, 'root', 'global') &&
            <Stack p={2} borderWidth='1px' rounded='md' bg='yellow.50' _dark={{ bg: 'yellow.950' }}>
              <Text fontWeight='bold'>As root, you can specify a trial ID directly</Text>
              <Input type='text' placeholder='Trial ID' value={rootTrialId} onChange={e => setRootTrialId(e.target.value)} />
              <Button
                disabled={!rootTrialId} onClick={() => { onSelected(rootTrialId); onClose() }}
                colorPalette='primary' size='sm'
              >Copy to trial
              </Button>
            </Stack>}

          {loading && <Box textAlign='center'>Loading trials...</Box>}
          {!loading && trials.length === 0 && <Box textAlign='center'>No trials found.</Box>}
          {!loading && trials.length > 0 &&
            <Box>
              {trials.map(t =>
                <Box key={t._id} p={2} borderWidth='1px' borderColor='border.default' rounded='md' mb={2} display='flex' justifyContent='space-between' alignItems='center'>
                  <Text>{t.name}</Text>
                  <Button size='sm' onClick={() => { onSelected(t._id); onClose() }} colorPalette='primary'>Select</Button>
                </Box>
              )}
            </Box>}
        </Stack>
      }
      footer={<Button onClick={onClose}>Cancel</Button>}
    />
  )
}

const CATEGORY_LABELS = {
  mental_health: 'Mental Health',
  sleep: 'Sleep',
  gastrointestinal: 'GI / Gastrointestinal',
  cognition: 'Cognition',
  wellbeing: 'Wellbeing',
  pain: 'Pain',
  fatigue: 'Fatigue',
  general_health: 'General Health',
  substance_use: 'Substance Use',
  physical_activity: 'Physical Activity',
  nutrition: 'Nutrition'
}

const CATEGORY_COLOURS = {
  mental_health: 'purple',
  sleep: 'blue',
  gastrointestinal: 'orange',
  cognition: 'teal',
  wellbeing: 'green',
  pain: 'red',
  fatigue: 'yellow',
  nutrition: 'green',
  general_health: 'cyan',
  substance_use: 'pink',
  physical_activity: 'green'
}

function QuestionnaireLibraryModal ({ isOpen, onClose, onImport, importing, trialId }) {
  const [questionnaires, setQuestionnaires] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [selected, setSelected] = useState(null)
  const [licenceAcknowledged, setLicenceAcknowledged] = useState(false)
  const [trialflareAcknowledged, setTrialflareAcknowledged] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setLoading(true)
    api.trials.getQuestionnaireLibrary(trialId, ({ questionnaires: qs }) => {
      setQuestionnaires(qs)
      setLoading(false)
    }, () => setLoading(false))
  }, [isOpen, trialId])

  function selectQuestionnaire (q) {
    setSelected(q)
    setLicenceAcknowledged(false)
    setTrialflareAcknowledged(false)
  }

  const categories = ['all', ...Array.from(new Set(questionnaires.map(q => q.category).filter(Boolean)))]

  const filtered = questionnaires.filter(q => {
    const matchesCategory = categoryFilter === 'all' || q.category === categoryFilter
    const term = search.toLowerCase()
    const matchesSearch = !term ||
      q.name.toLowerCase().includes(term) ||
      q.fullName.toLowerCase().includes(term) ||
      (q.description || '').toLowerCase().includes(term)
    return matchesCategory && matchesSearch
  })

  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={onClose}
      header='Questionnaire library'
      modalProps={{ size: '2xl' }}
      body={
        selected
          ? (
            <Stack gap={4}>
              <Button variant='ghost' size='sm' alignSelf='start' onClick={() => setSelected(null)}>← Back to library</Button>
              <Box>
                <Stack direction='row' alignItems='center' gap={2} flexWrap='wrap'>
                  <Heading as='h3' size='md'>{selected.fullName || selected.name}</Heading>
                  {selected.category &&
                    <Badge colorPalette={CATEGORY_COLOURS[selected.category] || 'gray'}>
                      {CATEGORY_LABELS[selected.category] || selected.category}
                    </Badge>}
                  {selected.autoScore &&
                    <Badge colorPalette='teal' variant='subtle'>Auto-score</Badge>}
                </Stack>
                <Text mt={2}>{selected.description}</Text>
              </Box>

              <Stack gap={2}>
                <Box p={3} borderWidth='1px' borderColor='border.default' rounded='md' bg='bg.subtle'>
                  <Text fontWeight='semibold' mb={1}>Licence</Text>
                  <Text>{selected.licence}</Text>
                  {selected.licenceDetails && <Text mt={1} fontSize='sm' color='fg.muted'>{selected.licenceDetails}</Text>}
                </Box>

                {selected.licenceRestrictions &&
                  <Alert status='warning'>
                    <Text fontWeight='semibold'>Licence restrictions: </Text>
                    <Text>{selected.licenceRestrictions}</Text>
                  </Alert>}

                {selected.validation &&
                  <Box p={3} borderWidth='1px' borderColor='border.default' rounded='md' bg='bg.subtle'>
                    <Text fontWeight='semibold' mb={1}>Validation</Text>
                    <Text fontSize='sm'>{selected.validation}</Text>
                  </Box>}

                {selected.scoringNotes &&
                  <Box p={3} borderWidth='1px' borderColor='border.default' rounded='md' bg='bg.subtle'>
                    <Text fontWeight='semibold' mb={1}>Scoring</Text>
                    <Text fontSize='sm'>{selected.scoringNotes}</Text>
                  </Box>}

                {selected.citation &&
                  <Box p={3} borderWidth='1px' borderColor='border.default' rounded='md' bg='bg.subtle'>
                    <Text fontWeight='semibold' mb={1}>Citation</Text>
                    <Text fontSize='sm' fontStyle='italic'>{selected.citation}</Text>
                  </Box>}
              </Stack>

              <Box p={3} borderWidth='1px' borderColor='border.warning' rounded='md' bg='orange.50' _dark={{ bg: 'orange.950' }}>
                <Stack gap={3}>
                  <Checkbox
                    checked={licenceAcknowledged}
                    onCheckedChange={({ checked }) => setLicenceAcknowledged(checked)}
                  >
                    <Text fontSize='sm'>
                      I confirm that I have reviewed the licence terms for this questionnaire, and that I accept responsibility for ensuring our use complies with all applicable restrictions and requirements, including obtaining any necessary licences or permissions from rights holders.
                    </Text>
                  </Checkbox>
                  <Checkbox
                    checked={trialflareAcknowledged}
                    onCheckedChange={({ checked }) => setTrialflareAcknowledged(checked)}
                  >
                    <Text fontSize='sm'>
                      I acknowledge that Trialflare does not distribute or verify licences for third-party questionnaires. I confirm that I have the rights to use this questionnaire within a software tool, such as Trialflare, for my research project.
                    </Text>
                  </Checkbox>
                </Stack>
              </Box>
            </Stack>
            )
          : (
            <Stack gap={4}>
              <Input
                placeholder='Search questionnaires…'
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <Stack direction='row' flexWrap='wrap' gap={2}>
                {categories.map(cat => (
                  <Button
                    key={cat}
                    size='xs'
                    variant={categoryFilter === cat ? 'solid' : 'outline'}
                    colorPalette={cat === 'all' ? 'primary' : (CATEGORY_COLOURS[cat] || 'gray')}
                    onClick={() => setCategoryFilter(cat)}
                  >
                    {cat === 'all' ? 'All' : (CATEGORY_LABELS[cat] || cat)}
                  </Button>
                ))}
              </Stack>

              {loading && <Text textAlign='center' color='fg.muted'>Loading…</Text>}

              {!loading && filtered.length === 0 &&
                <Text textAlign='center' color='fg.muted'>No questionnaires match your search.</Text>}

              {!loading && filtered.length > 0 &&
                <Stack gap={3}>
                  {filtered.map(q => (
                    <Box
                      key={q.id}
                      p={4}
                      borderWidth='1px'
                      borderColor='border.default'
                      rounded='md'
                      bg='bg.surface'
                      _hover={{ borderColor: 'border.emphasized' }}
                    >
                      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' gap={3}>
                        <Box flex='1'>
                          <Stack direction='row' alignItems='center' gap={2} flexWrap='wrap' mb={1}>
                            <Heading as='h4' size='sm'>{q.name}</Heading>
                            <Text fontSize='sm' color='fg.muted'>{q.fullName}</Text>
                            {q.category &&
                              <Badge colorPalette={CATEGORY_COLOURS[q.category] || 'gray'} size='sm'>
                                {CATEGORY_LABELS[q.category] || q.category}
                              </Badge>}
                            {q.autoScore &&
                              <Badge colorPalette='teal' size='sm' variant='subtle'>Auto-score</Badge>}
                          </Stack>
                          <Text fontSize='sm' noOfLines={2}>{q.description}</Text>
                          <Text fontSize='xs' color='fg.muted' mt={1}>Licence: {q.licence}</Text>
                        </Box>
                        <Button size='sm' colorPalette='primary' onClick={() => selectQuestionnaire(q)}>
                          View &amp; add
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                </Stack>}
            </Stack>
            )
      }
      footer={
        selected
          ? (
            <Stack direction='row' gap={2}>
              <Button variant='ghost' onClick={() => setSelected(null)} isDisabled={importing}>Back</Button>
              <Button
                colorPalette='primary'
                disabled={!licenceAcknowledged || !trialflareAcknowledged}
                loading={importing}
                onClick={() => onImport(selected.id)}
              >
                Add to trial
              </Button>
            </Stack>
            )
          : <Button onClick={onClose}>Close</Button>
      }
    />
  )
}
